import { motion } from 'framer-motion'
import {
  HiOutlineCreditCard,
  HiOutlineIdentification,
  HiOutlineKey,
} from 'react-icons/hi'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { isRtl, useLocale } from '@/lib/locale'
import { copy } from './Slide09SameGoals.copy'

const ease = [0.16, 1, 0.3, 1] as const
const transitionBase = { duration: 0.45, ease }
const animationProps = { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 } }

export default function Slide09SameGoals(_props: SlideProps) {
  const c = useSlideCopy(copy)
  // The Profit pill slides in "from the arrows", which is from the left in
  // LTR and from the right in RTL. Framer's `x` is physical.
  const pillFrom = isRtl(useLocale()) ? 10 : -10

  const cards = [
    { title: c.card1Title, body: c.card1Body, Icon: HiOutlineIdentification },
    { title: c.card2Title, body: c.card2Body, Icon: HiOutlineKey },
    { title: c.card3Title, body: c.card3Body, Icon: HiOutlineCreditCard },
  ]

  return (
    <section className="text-slate-900 min-h-[70vh] w-full flex flex-col justify-center">
      <div className="w-full max-w-4xl mx-auto">
        <div className="space-y-8">
          {/* Title + intro (left-aligned because of the flow layout below) */}
          <div className="space-y-2 text-start">
            <motion.div {...animationProps} transition={{ ...transitionBase, delay: 0.05 }}>
              <h2 className="text-5xl sm:text-6xl font-semibold text-slate-900 tracking-tight whitespace-pre-line leading-[1.1]">
                {c.title}
              </h2>
            </motion.div>
            <motion.div
              {...animationProps}
              transition={{ ...transitionBase, delay: 0.12 }}
              className="text-xl sm:text-2xl text-slate-600 leading-relaxed max-w-2xl"
            >
              {c.intro}
            </motion.div>
          </div>

          {/* 3 cards → arrows → Profit */}
          <div className="mt-12 w-full">
            <div className="grid grid-cols-[1fr_80px_auto] sm:grid-cols-[1fr_120px_auto] items-center gap-0">
              {/* Cards column */}
              <div className="flex flex-col gap-6">
                {cards.map((card, index) => (
                  <motion.div
                    key={card.title}
                    {...animationProps}
                    transition={{ ...transitionBase, delay: 0.25 + index * 0.1 }}
                    className="flex items-start gap-5 rounded-xl border border-slate-100 bg-slate-50/50 px-5 py-4"
                  >
                    <div className="mt-1 text-slate-900 shrink-0">
                      <card.Icon className="h-10 w-10 stroke-[0.75]" />
                    </div>
                    <div className="text-start">
                      <h3 className="text-2xl font-semibold text-slate-900">{card.title}</h3>
                      <p className="mt-1 text-lg text-slate-600 leading-relaxed">{card.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* SVG: three curving arrows converging to the Profit pill */}
              <motion.div
                {...animationProps}
                transition={{ ...transitionBase, delay: 0.55 }}
                className="hidden sm:block h-full"
              >
                {/* Arrows are drawn left-to-right; dir-flip mirrors them so
                    they still point at the pill under RTL. */}
                <svg
                  viewBox="0 0 120 200"
                  fill="none"
                  preserveAspectRatio="none"
                  className="dir-flip w-full h-full"
                >
                  <defs>
                    <marker
                      id="slide09-arrowhead"
                      markerWidth="8"
                      markerHeight="6"
                      refX="7"
                      refY="3"
                      orient="auto"
                    >
                      <path d="M0 0 L8 3 L0 6 Z" fill="#cbd5e1" />
                    </marker>
                  </defs>
                  <motion.path
                    d="M0 33 C60 33, 60 100, 112 100"
                    stroke="#cbd5e1"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    fill="none"
                    markerEnd="url(#slide09-arrowhead)"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.6, delay: 0.7, ease }}
                  />
                  <motion.path
                    d="M0 100 L112 100"
                    stroke="#cbd5e1"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    fill="none"
                    markerEnd="url(#slide09-arrowhead)"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.6, delay: 0.8, ease }}
                  />
                  <motion.path
                    d="M0 167 C60 167, 60 100, 112 100"
                    stroke="#cbd5e1"
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    fill="none"
                    markerEnd="url(#slide09-arrowhead)"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.6, delay: 0.9, ease }}
                  />
                </svg>
              </motion.div>

              {/* Profit pill */}
              <motion.div
                initial={{ opacity: 0, x: pillFrom }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 1.1, ease }}
                className="flex items-center justify-center"
              >
                <div className="rounded-lg p-4 sm:px-5 sm:py-4 border border-slate-200">
                  <p className="text-2xl sm:text-2xl font-semibold tracking-tight">
                    {c.target}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
